import React from 'react';
import '../css/DocumentationPage.css'; // Optional: Add a CSS file for styling

function DocumentationPage() {
  return (
    <div className="documentation-page">
      <header className="documentation-header">
        <h1>URL Shortener Documentation</h1>
        <p>Welcome to the documentation for the URL Shortener and Analytics platform!</p>
      </header>

      <main className="documentation-content">
        <section>
          <h2>Introduction</h2>
          <p>
            This application allows you to shorten URLs, track clicks, and gather analytics
            like unique visitors, total visits, and visitor countries.
          </p>
        </section>

        <section>
          <h2>Features</h2>
          <ul>
            <li>Shorten long URLs for easy sharing.</li>
            <li>Track total clicks and unique visitors per shortened URL.</li>
            <li>Visualize data using charts (pie chart, line chart, bar chart).</li>
            <li>Track the countries from which visitors are accessing your links.</li>
            <li>Retrieve detailed analytics for each shortened URL.</li>
          </ul>
        </section>

        <section>
          <h2>Getting Started</h2>
          <p>
            Follow these steps to get started with the application:
          </p>
          <ol>
            <li>
              Navigate to the dashboard after logging in.
            </li>
            <li>
              Use the "Shorten URL" form to create a new shortened link.
            </li>
            <li>
              Click on any shortened link in the dashboard to view detailed analytics.
            </li>
            <li>
              Access the visualizations, including charts showing link clicks over time.
            </li>
          </ol>
        </section>

        <section>
          <h2>API Endpoints</h2>
          <p>
            Below are the available API endpoints used in the application:
          </p>
          <ul>
            <li>
              <strong>POST /shorten</strong>: Create a shortened URL.
            </li>
            <li>
              <strong>GET /:shortUrl</strong>: Redirect to the original URL and track analytics.
            </li>
            <li>
              <strong>GET /api/charts/:shortUrl/daily</strong>: Retrieve daily analytics for a URL.
            </li>
            <li>
              <strong>GET /api/charts/:shortUrl/dailyUniqueVisitors</strong>: Retrieve daily unique visitor data.
            </li>
          </ul>
        </section>

        <section>
          <h2>Charts and Visualizations</h2>
          <p>
            The application provides the following visualizations:
          </p>
          <ul>
            <li>
              <strong>Pie Chart:</strong> Displays total link clicks for all shortened URLs.
            </li>
            <li>
              <strong>Line Chart:</strong> Shows link clicks over time for a selected URL.
            </li>
            <li>
              <strong>Bar Chart:</strong> Displays unique visitor data over time.
            </li>
          </ul>
        </section>

        <section>
          <h2>Error Handling</h2>
          <p>
            Common errors and their solutions:
          </p>
          <ul>
            <li>
              <strong>Link not found (404):</strong> Ensure the correct short URL is used.
            </li>
            <li>
              <strong>Server error (500):</strong> Check backend logs for details.
            </li>
          </ul>
        </section>

        <section>
          <h2>Technical Details</h2>
          <p>
            <strong>Frontend:</strong> Built with React, uses Chart.js for visualizations.<br />
            <strong>Backend:</strong> Powered by Express.js, integrates with MongoDB for data storage.<br />
            <strong>IP Geolocation:</strong> Uses the MaxMind GeoLite2 database to determine visitor countries.
          </p>
        </section>

        <section>
          <h2>FAQ</h2>
          <p>
            <strong>Q:</strong> How can I view analytics for a specific link?<br />
            <strong>A:</strong> Click on the link in the dashboard to view detailed charts and visitor data.
          </p>
          <p>
            <strong>Q:</strong> How is visitor country data obtained?<br />
            <strong>A:</strong> The backend uses the MaxMind GeoLite2 database to look up the country based on the visitor's IP address.
          </p>
        </section>

        <section>
          <h2>Contact</h2>
          <p>
            For support or inquiries, please email us at <a href="mailto:support@example.com">support@example.com</a>.
          </p>
        </section>
      </main>

      <footer className="documentation-footer">
        <p>&copy; {new Date().getFullYear()} URL Shortener Platform</p>
      </footer>
    </div>
  );
}

export default DocumentationPage;
